import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { specials } from "@/mocks/specials";
import { reviews } from "@/mocks/reviews";
import { dramas } from "@/mocks/dramas";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import ReviewArticle from "@/components/feature/ReviewArticle";
import EmptyState from "@/components/feature/EmptyState";

export default function Special() {
  useSeo({
    title: "影评专题 - 出轨的妻子影视大全",
    description:
      "出轨的妻子影视影评专题页，汇集多篇围绕婚姻、信任与情感选择的深度分析文章与专辑策划，按主题分组呈现，帮你读懂剧情背后的表达。",
    keywords: "出轨的妻子影评,影评专题,深度解读,情感剧分析,影视策划,角色剖析",
  });

  const [activeId, setActiveId] = useState(specials[0]?.id ?? "");
  const active = specials.find((s) => s.id === activeId) ?? specials[0];

  if (!active) {
    return (
      <div className="mx-auto max-w-5xl px-4 md:px-6 py-16">
        <EmptyState title="暂无专题文章" description="专题内容正在筹备中，敬请期待。" />
      </div>
    );
  }

  const relatedDramas = dramas.filter((d) => active.dramaIds.includes(d.id));

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影评专题" }]} />

      <header className="mt-6 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">影评专题</h1>
        <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600 max-w-3xl">
          这里汇集了围绕「出轨的妻子」题材的深度影评与专辑策划。我们按主题分组，把剧情、人物与表达串联起来，
          帮助你从不同角度理解这些作品想说的话。
        </p>
      </header>

      {/* Special tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {specials.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveId(s.id)}
            className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
              activeId === s.id
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 text-foreground-700 hover:border-primary-300"
            }`}
          >
            {s.group} · {s.title.slice(0, 8)}
          </button>
        ))}
      </div>

      {/* Active special */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
        <div className="relative rounded-lg overflow-hidden min-h-[240px]">
          <img
            src={active.cover}
            alt={`${active.title} 专题配图`}
            title={`${active.title}·影视专题`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <span className="self-start rounded-full bg-accent-500 px-3 py-0.5 text-[11px] font-medium text-white mb-2">
              {active.group}
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-white">{active.title}</h2>
          </div>
        </div>

        <div className="flex flex-col">
          <p className="text-sm md:text-base leading-8 text-foreground-700">{active.intro}</p>

          <div className="mt-6 space-y-3 flex-1">
            {active.articles.map((article) => (
              <div
                key={article.id}
                className="rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-6">
                    {article.title}
                  </h3>
                  <span className="shrink-0 text-[11px] text-foreground-400 whitespace-nowrap">{article.date}</span>
                </div>
                <p className="mt-1.5 text-xs leading-5 text-foreground-600 line-clamp-2">{article.excerpt}</p>
                <p className="mt-2 text-[11px] text-foreground-500 flex items-center gap-1">
                  <i className="ri-user-line" />
                  {article.author}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <p className="text-xs font-medium text-foreground-500 mb-2.5">专题相关影视</p>
            <div className="flex flex-wrap gap-2">
              {relatedDramas.map((d) => (
                <Link
                  key={d.id}
                  to={`/detail/${d.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-film-line" />
                  {d.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All reviews */}
      <section className="mt-16">
        <SectionHeading
          title="全部深度影评"
          subtitle="点击展开全文，阅读完整解读"
        />
        <div className="space-y-5">
          {reviews.map((review) => (
            <ReviewArticle key={review.id} review={review} anchorId={review.id} />
          ))}
        </div>
      </section>

      {/* Back nav */}
      <div className="mt-12 flex flex-wrap justify-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
        <Link
          to="/category"
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-grid-line" />
          浏览影视分类
        </Link>
      </div>
    </div>
  );
}